import React, { useState } from 'react';
import ModuleLayout from '../../layouts/ModuleLayout';
import {
  Calendar,
  Clock,
  Users,
  Plus,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Copy,
  Trash2,
  Edit2,
  Sun,
  Moon,
  Sunrise,
  Sunset,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Download,
  Upload,
  Send,
  Filter,
  Search,
  UserPlus,
  ArrowLeftRight,
  Coffee,
  Utensils,
  X,
  Check,
  FileText,
  Settings,
  BarChart3,
  TrendingUp,
  DollarSign,
  Zap,
} from 'lucide-react';

// Dummy shift templates
const shiftTemplates = [
  { id: 1, name: 'Morning Shift', code: 'MS', startTime: '06:00', endTime: '14:00', color: 'amber', icon: Sunrise },
  { id: 2, name: 'Day Shift', code: 'DS', startTime: '09:00', endTime: '18:00', color: 'blue', icon: Sun },
  { id: 3, name: 'Evening Shift', code: 'ES', startTime: '14:00', endTime: '22:00', color: 'orange', icon: Sunset },
  { id: 4, name: 'Night Shift', code: 'NS', startTime: '22:00', endTime: '06:00', color: 'indigo', icon: Moon },
  { id: 5, name: 'Split Shift', code: 'SS', startTime: '11:00', endTime: '15:00 & 18:00-22:00', color: 'violet', icon: RefreshCw },
];

// Dummy weekly schedule
const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const weekDates = ['30', '31', '1', '2', '3', '4', '5'];

const employees = [
  { id: 1, name: 'Rahul Sharma', role: 'Head Chef', department: 'Kitchen', shifts: ['MS', 'MS', 'MS', 'MS', 'MS', 'OFF', 'OFF'] },
  { id: 2, name: 'Priya Patel', role: 'Floor Manager', department: 'Service', shifts: ['DS', 'DS', 'DS', 'DS', 'DS', 'DS', 'OFF'] },
  { id: 3, name: 'Amit Kumar', role: 'Sous Chef', department: 'Kitchen', shifts: ['ES', 'ES', 'ES', 'ES', 'OFF', 'ES', 'ES'] },
  { id: 4, name: 'Sneha Reddy', role: 'Waiter', department: 'Service', shifts: ['DS', 'DS', 'OFF', 'DS', 'DS', 'ES', 'ES'] },
  { id: 5, name: 'Vikram Singh', role: 'Line Cook', department: 'Kitchen', shifts: ['MS', 'MS', 'MS', 'OFF', 'OFF', 'MS', 'MS'] },
  { id: 6, name: 'Anita Verma', role: 'Cashier', department: 'Admin', shifts: ['DS', 'DS', 'DS', 'DS', 'DS', 'OFF', 'OFF'] },
  { id: 7, name: 'Kiran Joshi', role: 'Waiter', department: 'Service', shifts: ['ES', 'ES', 'OFF', 'OFF', 'ES', 'ES', 'ES'] },
  { id: 8, name: 'Deepak Nair', role: 'Prep Cook', department: 'Kitchen', shifts: ['MS', 'MS', 'MS', 'MS', 'MS', 'MS', 'OFF'] },
];

const shiftRequests = [
  { id: 1, from: 'Priya Patel', to: 'Sneha Reddy', date: 'Wed, 1 Jan', shift: 'DS', type: 'swap', status: 'pending' },
  { id: 2, from: 'Amit Kumar', type: 'timeoff', date: 'Sat, 4 Jan', reason: 'Family event', status: 'pending' },
  { id: 3, from: 'Vikram Singh', to: 'Deepak Nair', date: 'Thu, 2 Jan', shift: 'MS', type: 'pickup', status: 'approved' },
];

const openShifts = [
  { id: 1, date: 'Fri, 3 Jan', shift: 'ES', department: 'Service', role: 'Waiter', applicants: 2 },
  { id: 2, date: 'Sat, 4 Jan', shift: 'MS', department: 'Kitchen', role: 'Line Cook', applicants: 0 },
  { id: 3, date: 'Sun, 5 Jan', shift: 'ES', department: 'Service', role: 'Waiter', applicants: 1 },
];

const TABS = [
  { id: 'schedule', label: 'Weekly Schedule', icon: Calendar },
  { id: 'templates', label: 'Shift Templates', icon: Clock },
  { id: 'requests', label: 'Requests', icon: ArrowLeftRight },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function ShiftManagementPage() {
  const [activeTab, setActiveTab] = useState('schedule');
  const [currentWeek, setCurrentWeek] = useState('Dec 30, 2024 - Jan 5, 2025');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [selectedCell, setSelectedCell] = useState(null);

  const getShiftBadge = (shiftCode) => {
    if (shiftCode === 'OFF') {
      return (
        <span className="inline-flex items-center justify-center w-full py-1.5 text-xs font-medium text-gray-400 bg-gray-100 rounded">
          OFF
        </span>
      );
    }
    const shift = shiftTemplates.find(s => s.code === shiftCode);
    if (!shift) return null;

    const colorClasses = {
      amber: 'bg-amber-100 text-amber-700 border-amber-200',
      blue: 'bg-blue-100 text-blue-700 border-blue-200',
      orange: 'bg-orange-100 text-orange-700 border-orange-200',
      indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      violet: 'bg-violet-100 text-violet-700 border-violet-200',
    };

    return (
      <span className={`inline-flex items-center justify-center w-full py-1.5 text-xs font-medium rounded border ${colorClasses[shift.color]}`}>
        {shiftCode}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
      approved: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
    };
    const { bg, text, label } = config[status];
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${bg} ${text}`}>
        {label}
      </span>
    );
  };

  const filteredEmployees = filterDepartment === 'all'
    ? employees
    : employees.filter(emp => emp.department === filterDepartment);

  return (
    <ModuleLayout>
      <div className="min-h-full bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 pt-5 pb-0">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Shift Management</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Schedule shifts, manage swaps & track coverage
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button className="p-1.5 hover:bg-white rounded transition">
                    <ChevronLeft size={16} className="text-gray-600" />
                  </button>
                  <span className="px-3 text-sm font-medium text-gray-700">{currentWeek}</span>
                  <button className="p-1.5 hover:bg-white rounded transition">
                    <ChevronRight size={16} className="text-gray-600" />
                  </button>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm">
                  <Send size={16} />
                  Publish Schedule
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 -mb-px">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
                      isActive
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="font-medium text-sm">{tab.label}</span>
                    {tab.id === 'requests' && (
                      <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full font-medium">2</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'schedule' && (
            <div className="space-y-4">
              {/* Toolbar */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <select
                      value={filterDepartment}
                      onChange={(e) => setFilterDepartment(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Departments</option>
                      <option value="Kitchen">Kitchen</option>
                      <option value="Service">Service</option>
                      <option value="Admin">Admin</option>
                    </select>
                    <div className="flex items-center gap-2 text-sm">
                      {shiftTemplates.slice(0, 4).map(shift => {
                        const colorClasses = {
                          amber: 'bg-amber-100 text-amber-700',
                          blue: 'bg-blue-100 text-blue-700',
                          orange: 'bg-orange-100 text-orange-700',
                          indigo: 'bg-indigo-100 text-indigo-700',
                        };
                        return (
                          <span key={shift.id} className={`px-2 py-1 rounded text-xs font-medium ${colorClasses[shift.color]}`}>
                            {shift.code}: {shift.startTime}-{shift.endTime.split(' ')[0]}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition">
                      <Copy size={14} />
                      Copy Week
                    </button>
                    <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition">
                      <Zap size={14} />
                      Auto-Schedule
                    </button>
                  </div>
                </div>
              </div>

              {/* Schedule Grid */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[200px] sticky left-0 bg-gray-50 z-10">
                          Employee
                        </th>
                        {weekDays.map((day, idx) => (
                          <th key={day} className="px-2 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[80px]">
                            <div>{day}</div>
                            <div className="text-gray-400 font-normal mt-0.5">{weekDates[idx]}</div>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[80px]">
                          Hours
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredEmployees.map((emp) => {
                        const totalHours = emp.shifts.reduce((acc, shift) => {
                          if (shift === 'OFF') return acc;
                          const template = shiftTemplates.find(s => s.code === shift);
                          return acc + (template ? 8 : 0);
                        }, 0);

                        return (
                          <tr key={emp.id} className="hover:bg-gray-50 transition group">
                            <td className="px-4 py-3 sticky left-0 bg-white group-hover:bg-gray-50 z-10">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-xs">
                                  {emp.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900 text-sm">{emp.name}</p>
                                  <p className="text-xs text-gray-500">{emp.role}</p>
                                </div>
                              </div>
                            </td>
                            {emp.shifts.map((shift, idx) => (
                              <td key={idx} className="px-2 py-2">
                                <button
                                  className="w-full hover:opacity-80 transition"
                                  onClick={() => setSelectedCell({ empId: emp.id, day: idx })}
                                >
                                  {getShiftBadge(shift)}
                                </button>
                              </td>
                            ))}
                            <td className="px-4 py-3 text-center">
                              <span className={`text-sm font-medium ${totalHours > 40 ? 'text-amber-600' : 'text-gray-900'}`}>
                                {totalHours}h
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Open Shifts */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Open Shifts</h3>
                    <p className="text-xs text-gray-500">Unfilled shifts needing coverage</p>
                  </div>
                  <button className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
                    <Plus size={14} />
                    Add Open Shift
                  </button>
                </div>
                <div className="divide-y divide-gray-100">
                  {openShifts.map((shift) => {
                    const template = shiftTemplates.find(s => s.code === shift.shift);
                    const colorClasses = {
                      amber: 'bg-amber-100 text-amber-700',
                      blue: 'bg-blue-100 text-blue-700',
                      orange: 'bg-orange-100 text-orange-700',
                      indigo: 'bg-indigo-100 text-indigo-700',
                    };
                    return (
                      <div key={shift.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${template ? colorClasses[template.color] : 'bg-gray-100 text-gray-600'}`}>
                            {template && <template.icon size={18} />}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{shift.date}</p>
                            <p className="text-xs text-gray-500">{shift.department} • {shift.role} • {template?.startTime}-{template?.endTime?.split(' ')[0]}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {shift.applicants > 0 ? (
                            <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full font-medium">
                              {shift.applicants} applicant{shift.applicants > 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full font-medium">
                              No applicants
                            </span>
                          )}
                          <button className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition">
                            Assign
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="space-y-6">
              {/* Shift Templates */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {shiftTemplates.map((shift) => {
                  const Icon = shift.icon;
                  const colorClasses = {
                    amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                    blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
                    orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200' },
                    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' },
                    violet: { bg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200' },
                  };
                  const colors = colorClasses[shift.color];
                  return (
                    <div key={shift.id} className={`bg-white rounded-xl border ${colors.border} p-5 hover:shadow-md transition`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center`}>
                          <Icon size={24} className={colors.text} />
                        </div>
                        <div className="flex items-center gap-1">
                          <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
                            <Edit2 size={14} />
                          </button>
                          <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{shift.name}</h3>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${colors.bg} ${colors.text}`}>
                          {shift.code}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Timing</span>
                          <span className="font-medium text-gray-900">{shift.startTime} - {shift.endTime}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Duration</span>
                          <span className="font-medium text-gray-900">8 hours</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Break</span>
                          <span className="font-medium text-gray-900">1 hour</span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Add Template Card */}
                <button className="border-2 border-dashed border-gray-300 rounded-xl p-5 flex flex-col items-center justify-center hover:border-blue-400 hover:bg-blue-50/50 transition group min-h-[200px]">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-blue-100 transition">
                    <Plus size={24} className="text-gray-400 group-hover:text-blue-600 transition" />
                  </div>
                  <p className="font-medium text-gray-600 group-hover:text-blue-600 transition">Create New Shift</p>
                  <p className="text-xs text-gray-400 mt-1">Add a new shift template</p>
                </button>
              </div>

              {/* Break Rules */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Break Rules</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Coffee size={20} className="text-amber-500" />
                    <div>
                      <p className="font-medium text-gray-900">Lunch Break</p>
                      <p className="text-xs text-gray-500">1 hour (Paid)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Utensils size={20} className="text-emerald-500" />
                    <div>
                      <p className="font-medium text-gray-900">Tea Break</p>
                      <p className="text-xs text-gray-500">15 mins x 2 (Paid)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Clock size={20} className="text-blue-500" />
                    <div>
                      <p className="font-medium text-gray-900">Rest Period</p>
                      <p className="text-xs text-gray-500">Min 11 hrs between shifts</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="space-y-6">
              {/* Pending Requests */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Shift Requests</h3>
                    <p className="text-xs text-gray-500">Swap, pickup & time-off requests</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
                      <option>All Types</option>
                      <option>Swap</option>
                      <option>Pickup</option>
                      <option>Time Off</option>
                    </select>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {shiftRequests.map((req) => (
                    <div key={req.id} className="px-4 py-4 hover:bg-gray-50 transition">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            req.type === 'swap' ? 'bg-blue-100 text-blue-600' :
                            req.type === 'pickup' ? 'bg-emerald-100 text-emerald-600' :
                            'bg-amber-100 text-amber-600'
                          }`}>
                            {req.type === 'swap' && <ArrowLeftRight size={18} />}
                            {req.type === 'pickup' && <UserPlus size={18} />}
                            {req.type === 'timeoff' && <Coffee size={18} />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-gray-900">{req.from}</p>
                              {req.to && (
                                <>
                                  <ArrowLeftRight size={14} className="text-gray-400" />
                                  <p className="font-medium text-gray-900">{req.to}</p>
                                </>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">
                              {req.type === 'swap' && `Swap request for ${req.date} (${req.shift})`}
                              {req.type === 'pickup' && `Shift pickup for ${req.date} (${req.shift})`}
                              {req.type === 'timeoff' && `Time off request for ${req.date}`}
                            </p>
                            {req.reason && (
                              <p className="text-xs text-gray-400 mt-1">Reason: {req.reason}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(req.status)}
                          {req.status === 'pending' && (
                            <div className="flex items-center gap-2">
                              <button className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition" title="Approve">
                                <Check size={18} />
                              </button>
                              <button className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition" title="Reject">
                                <X size={18} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Request Type Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <ArrowLeftRight size={24} className="text-blue-600 mb-2" />
                  <h4 className="font-semibold text-gray-900">Shift Swap</h4>
                  <p className="text-sm text-gray-600 mt-1">Exchange shifts between employees</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <UserPlus size={24} className="text-emerald-600 mb-2" />
                  <h4 className="font-semibold text-gray-900">Shift Pickup</h4>
                  <p className="text-sm text-gray-600 mt-1">Claim open or dropped shifts</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <Coffee size={24} className="text-amber-600 mb-2" />
                  <h4 className="font-semibold text-gray-900">Time Off</h4>
                  <p className="text-sm text-gray-600 mt-1">Request leave or personal time</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Clock size={20} className="text-blue-600" />
                    <TrendingUp size={16} className="text-emerald-500" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">324</p>
                  <p className="text-sm text-gray-500">Total Scheduled Hours</p>
                  <p className="text-xs text-emerald-600 mt-1">+5% from last week</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Users size={20} className="text-violet-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">94%</p>
                  <p className="text-sm text-gray-500">Shift Coverage</p>
                  <p className="text-xs text-amber-600 mt-1">3 open shifts</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <DollarSign size={20} className="text-emerald-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">₹42.5K</p>
                  <p className="text-sm text-gray-500">Est. Labor Cost</p>
                  <p className="text-xs text-gray-400 mt-1">This week</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <AlertTriangle size={20} className="text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">2</p>
                  <p className="text-sm text-gray-500">Overtime Alerts</p>
                  <p className="text-xs text-amber-600 mt-1">Employees near 48h limit</p>
                </div>
              </div>

              {/* Department Coverage */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Department Coverage</h3>
                <div className="space-y-4">
                  {[
                    { dept: 'Kitchen', scheduled: 128, required: 140, coverage: 91 },
                    { dept: 'Service', scheduled: 112, required: 112, coverage: 100 },
                    { dept: 'Admin', scheduled: 40, required: 40, coverage: 100 },
                  ].map((dept) => (
                    <div key={dept.dept} className="flex items-center gap-4">
                      <div className="w-24">
                        <p className="font-medium text-gray-900">{dept.dept}</p>
                        <p className="text-xs text-gray-500">{dept.scheduled}/{dept.required}h</p>
                      </div>
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${dept.coverage >= 100 ? 'bg-emerald-500' : dept.coverage >= 90 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.min(dept.coverage, 100)}%` }}
                        />
                      </div>
                      <span className={`text-sm font-medium ${dept.coverage >= 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {dept.coverage}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reports */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { title: 'Shift Coverage Report', desc: 'Filled vs unfilled shifts', icon: CheckCircle2, color: 'emerald' },
                  { title: 'Labor Cost Analysis', desc: 'Actual vs budgeted labor', icon: DollarSign, color: 'blue' },
                  { title: 'Employee Hours', desc: 'Weekly hours per employee', icon: Clock, color: 'violet' },
                ].map((report, idx) => {
                  const Icon = report.icon;
                  const colorClasses = {
                    emerald: 'bg-emerald-100 text-emerald-600',
                    blue: 'bg-blue-100 text-blue-600',
                    violet: 'bg-violet-100 text-violet-600',
                  };
                  return (
                    <button
                      key={idx}
                      className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:shadow-md hover:border-blue-300 transition group"
                    >
                      <div className={`w-10 h-10 ${colorClasses[report.color]} rounded-lg flex items-center justify-center mb-3`}>
                        <Icon size={20} />
                      </div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition">{report.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{report.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Scheduling Rules */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Scheduling Rules</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Configure scheduling constraints</p>
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Max Hours per Week</p>
                      <p className="text-xs text-gray-500">Per employee limit</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" defaultValue={48} className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm" />
                      <span className="text-sm text-gray-500">hours</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Min Rest Between Shifts</p>
                      <p className="text-xs text-gray-500">Required gap between shifts</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" defaultValue={11} className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm" />
                      <span className="text-sm text-gray-500">hours</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Max Consecutive Days</p>
                      <p className="text-xs text-gray-500">Without a day off</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" defaultValue={6} className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm" />
                      <span className="text-sm text-gray-500">days</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Overtime Alert At</p>
                      <p className="text-xs text-gray-500">Hours threshold for OT warning</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" defaultValue={40} className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm" />
                      <span className="text-sm text-gray-500">hours</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Notifications</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Configure shift notifications</p>
                </div>
                <div className="p-4 space-y-3">
                  {[
                    { label: 'Schedule Published', desc: 'Notify employees when new schedule is published', enabled: true },
                    { label: 'Shift Reminder', desc: 'Remind employees before their shift starts', enabled: true },
                    { label: 'Swap Requests', desc: 'Notify when someone requests a swap', enabled: true },
                    { label: 'Open Shift Alert', desc: 'Alert eligible employees about open shifts', enabled: false },
                    { label: 'Overtime Warning', desc: 'Alert when employee approaches OT limit', enabled: true },
                  ].map((setting, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{setting.label}</p>
                        <p className="text-xs text-gray-500">{setting.desc}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={setting.enabled} className="sr-only peer" readOnly />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ModuleLayout>
  );
}
